/* HUSL product reviews — storefront widget.
   Mounts two things when present on the page:
     #husl-reviews  PDP widget: summary, histogram filters, cards, lightbox, inline write form
     #hpr           token form (/pages/product-review?token=…)
   Talks to the reviews Worker (docs/product-reviews.md). Vanilla ES2017, no dependencies. */
(function () {
  'use strict';

  var STAR_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2l2.9 6.26L21.8 9.1l-5 4.73 1.3 6.87L12 17.4l-6.1 3.3 1.3-6.87-5-4.73 6.9-.84z"/></svg>';
  var PLAY_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  var LIMITS = { images: 5, videos: 1, imageBytes: 10 * 1024 * 1024, videoBytes: 100 * 1024 * 1024 };
  var IMG_RE = /^image\/(jpeg|png|webp|heic|heif)$/i, VID_RE = /^video\/(mp4|quicktime|webm)$/i;
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var GENERIC_ERR = 'Something went wrong — please try again.';

  /* ---------- utils ---------- */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function el(html) { var t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstChild; }
  function q(root, sel) { return root.querySelector(sel); }
  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function plural(n, w) { return n + ' ' + w + (n === 1 ? '' : 's'); }
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmtDate(iso) { var d = new Date(iso); return isNaN(d) ? '' : MONTHS[d.getMonth()] + ' ' + d.getFullYear(); }
  function starsHTML(r, size) {
    r = Math.max(0, Math.min(5, Number(r) || 0));
    return '<span class="prv-stars" style="font-size:' + (size || 16) + 'px" role="img" aria-label="Rated ' + r + ' out of 5 stars">' +
      '<span class="prv-stars__bg">&#9733;&#9733;&#9733;&#9733;&#9733;</span><span class="prv-stars__fg" style="width:' + (r * 20) + '%">&#9733;&#9733;&#9733;&#9733;&#9733;</span></span>';
  }
  /* JSON fetch that never throws on HTTP errors: resolves {ok, status, data}. Network errors reject. */
  function api(base, path, opts) {
    opts = opts || {};
    var init = { method: opts.method || 'GET', headers: {} };
    if (opts.body) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(opts.body); }
    return fetch(base + path, init).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, status: r.status, data: data }; });
    });
  }

  /* ---------- 44px star picker (radiogroup) ---------- */
  function starPicker(label, onChange) {
    var node = el('<div class="prv-pick"><div class="prv-pick__stars" role="radiogroup" aria-label="' + esc(label) + '"></div><span class="prv-pick__val" aria-live="polite"></span></div>');
    var group = q(node, '.prv-pick__stars'), val = q(node, '.prv-pick__val'), value = 0;
    var words = ['', 'Poor', 'Fair', 'Good', 'Great', 'Love it'];
    for (var i = 1; i <= 5; i++) group.appendChild(el('<button type="button" class="prv-pick__star" role="radio" aria-checked="false" data-v="' + i + '" aria-label="' + plural(i, 'star') + '">' + STAR_SVG + '</button>'));
    function paint(v) { qa(group, 'button').forEach(function (b) { b.classList.toggle('on', Number(b.dataset.v) <= v); }); }
    function set(v) {
      value = v; paint(v); val.textContent = words[v] || '';
      qa(group, 'button').forEach(function (b) { b.setAttribute('aria-checked', String(Number(b.dataset.v) === v)); b.tabIndex = (v ? Number(b.dataset.v) === v : Number(b.dataset.v) === 1) ? 0 : -1; });
      if (onChange) onChange(v);
    }
    group.addEventListener('mouseover', function (e) { var b = e.target.closest('button'); if (b) paint(Number(b.dataset.v)); });
    group.addEventListener('mouseleave', function () { paint(value); });
    group.addEventListener('click', function (e) { var b = e.target.closest('button'); if (b) set(Number(b.dataset.v)); });
    group.addEventListener('keydown', function (e) {
      var d = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -1 : 0;
      if (!d) return; e.preventDefault(); set(Math.max(1, Math.min(5, (value || 0) + d))); q(group, '[data-v="' + value + '"]').focus();
    });
    set(0);
    return { node: node, get: function () { return value; } };
  }

  /* ---------- lightbox (singleton, shared by every review on the page) ---------- */
  var LB = (function () {
    var node, stage, cap, items = [], idx = 0, opener = null, tx = null;
    function build() {
      node = el('<div class="prv-lb" role="dialog" aria-modal="true" aria-label="Review photos and videos" hidden>' +
        '<button type="button" class="prv-lb__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="prv-lb__nav prv-lb__nav--prev" aria-label="Previous">&#8249;</button>' +
        '<div class="prv-lb__stage"></div>' +
        '<button type="button" class="prv-lb__nav prv-lb__nav--next" aria-label="Next">&#8250;</button>' +
        '<p class="prv-lb__cap" aria-live="polite"></p></div>');
      stage = q(node, '.prv-lb__stage'); cap = q(node, '.prv-lb__cap');
      q(node, '.prv-lb__close').addEventListener('click', close);
      q(node, '.prv-lb__nav--prev').addEventListener('click', function () { show(idx - 1); });
      q(node, '.prv-lb__nav--next').addEventListener('click', function () { show(idx + 1); });
      node.addEventListener('click', function (e) { if (e.target === node || e.target === stage) close(); });
      node.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') show(idx - 1);
        else if (e.key === 'ArrowRight') show(idx + 1);
        else if (e.key === 'Tab') { /* keep focus inside the dialog */
          var f = qa(node, 'button, video'), first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      node.addEventListener('touchstart', function (e) { tx = e.changedTouches[0].clientX; }, { passive: true });
      node.addEventListener('touchend', function (e) { if (tx == null) return; var dx = e.changedTouches[0].clientX - tx; tx = null; if (Math.abs(dx) > 40) show(idx + (dx < 0 ? 1 : -1)); }, { passive: true });
      document.body.appendChild(node);
    }
    function show(i) {
      if (!items.length) return;
      idx = (i + items.length) % items.length;
      var m = items[idx];
      stage.innerHTML = m.type === 'video'
        ? '<video controls playsinline autoplay src="' + esc(m.url) + '"' + (m.poster ? ' poster="' + esc(m.poster) + '"' : '') + '></video>'
        : '<img src="' + esc(m.url) + '" alt="' + esc(m.alt || 'Customer photo') + '">';
      cap.textContent = (idx + 1) + ' of ' + items.length + (m.by ? ' · ' + m.by : '');
      node.classList.toggle('is-single', items.length < 2);
    }
    function open(list, i, openerEl) {
      if (!node) build();
      items = list; opener = openerEl || document.activeElement;
      node.hidden = false; document.body.classList.add('prv-noscroll');
      show(i || 0); q(node, '.prv-lb__close').focus();
    }
    function close() {
      if (!node || node.hidden) return;
      node.hidden = true; stage.innerHTML = ''; document.body.classList.remove('prv-noscroll');
      if (opener && opener.focus) opener.focus();
    }
    return { open: open, close: close };
  })();

  /* ---------- Turnstile: lazy script, explicit render, single-use tokens ---------- */
  var tsLoad = null;
  function turnstileWidget(container, sitekey) {
    if (!tsLoad) tsLoad = new Promise(function (res, rej) {
      if (window.turnstile) return res();
      var s = document.createElement('script'); s.src = TURNSTILE_SRC; s.async = true; s.defer = true;
      s.onload = function () { res(); }; s.onerror = function () { tsLoad = null; rej(new Error('turnstile')); };
      document.head.appendChild(s);
    });
    var resolve, pending, id;
    function arm() { pending = new Promise(function (r) { resolve = r; }); }
    arm();
    var ready = tsLoad.then(function () {
      id = window.turnstile.render(container, {
        sitekey: sitekey, appearance: 'interaction-only',
        callback: function (t) { resolve(t); },
        'expired-token-callback': function () { arm(); window.turnstile.reset(id); },
        'error-callback': function () { return true; }
      });
    });
    return {
      /* Resolve with a fresh token, then reset so the next call gets a new one (tokens are single-use). */
      take: function (timeoutMs) {
        return ready.then(function () {
          return new Promise(function (res, rej) {
            var t = setTimeout(function () { rej(new Error('turnstile_timeout')); }, timeoutMs || 20000);
            pending.then(function (tok) { clearTimeout(t); arm(); try { window.turnstile.reset(id); } catch (e) { /* ignore */ } res(tok); });
          });
        });
      }
    };
  }

  /* ---------- uploader: drop zone, thumbnails, per-file progress, limits ---------- */
  function Uploader(opts) { /* opts: { api, getSession: () => Promise<session_id> } */
    var self = this, session = null;
    this.items = [];
    this.node = el('<div class="prv-up">' +
      '<input type="file" class="prv-up__input" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm" hidden>' +
      '<button type="button" class="prv-up__zone"><span class="prv-up__cta">Add photos or a video</span><span class="prv-up__hint">Up to 5 photos (10 MB each) and 1 video (100 MB). Drag and drop or tap.</span></button>' +
      '<ul class="prv-up__list"></ul><p class="prv-up__err" role="alert"></p></div>');
    var input = q(this.node, '.prv-up__input'), zone = q(this.node, '.prv-up__zone'), list = q(this.node, '.prv-up__list'), err = q(this.node, '.prv-up__err');
    zone.addEventListener('click', function () { input.click(); });
    input.addEventListener('change', function () { self.add(input.files); input.value = ''; });
    ['dragenter', 'dragover'].forEach(function (t) { zone.addEventListener(t, function (e) { e.preventDefault(); zone.classList.add('is-over'); }); });
    ['dragleave', 'drop'].forEach(function (t) { zone.addEventListener(t, function (e) { e.preventDefault(); zone.classList.remove('is-over'); }); });
    zone.addEventListener('drop', function (e) { if (e.dataTransfer) self.add(e.dataTransfer.files); });
    list.addEventListener('click', function (e) { var b = e.target.closest('[data-remove]'); if (b) self.remove(b.closest('li').__item); });

    function count(type) { return self.items.filter(function (i) { return i.type === type; }).length; }
    function getSession() {
      if (!session) session = opts.getSession().catch(function (e) { session = null; throw e; });
      return session;
    }
    this.add = function (files) {
      err.textContent = '';
      Array.prototype.forEach.call(files, function (file) {
        var type = IMG_RE.test(file.type) ? 'image' : VID_RE.test(file.type) ? 'video' : null;
        if (!type) return (err.textContent = 'Only JPG, PNG, WebP, HEIC photos and MP4, MOV, WebM videos are accepted.');
        if (type === 'image' && count('image') >= LIMITS.images) return (err.textContent = 'You can add up to ' + LIMITS.images + ' photos.');
        if (type === 'video' && count('video') >= LIMITS.videos) return (err.textContent = 'You can add one video per review.');
        if (file.size > (type === 'image' ? LIMITS.imageBytes : LIMITS.videoBytes)) return (err.textContent = file.name + ' is too large (max ' + (type === 'image' ? '10' : '100') + ' MB).');
        var item = { file: file, type: type, status: 'queued', gid: null, url: URL.createObjectURL(file) };
        item.node = el('<li class="prv-up__item">' +
          (type === 'video' ? '<video class="prv-up__thumb" muted playsinline src="' + item.url + '"></video>' : '<img class="prv-up__thumb" alt="" src="' + item.url + '">') +
          '<span class="prv-up__bar"><span class="prv-up__fill"></span></span><span class="prv-up__status"></span>' +
          '<button type="button" class="prv-up__remove" data-remove aria-label="Remove ' + esc(file.name) + '">&times;</button></li>');
        item.node.__item = item; list.appendChild(item.node); self.items.push(item);
        q(item.node, '.prv-up__thumb').onerror = function () { this.remove(); }; /* e.g. HEIC: browser can't preview, tile still shows progress */
        upload(item);
      });
    };
    function setStatus(item, status, pct, text) {
      item.status = status; item.node.dataset.status = status;
      q(item.node, '.prv-up__fill').style.width = (pct || 0) + '%';
      q(item.node, '.prv-up__status').textContent = text || '';
    }
    function upload(item) {
      setStatus(item, 'uploading', 0, 'Preparing…');
      getSession().then(function (sid) {
        return new Promise(function (res, rej) {
          var xhr = new XMLHttpRequest(); item.xhr = xhr;
          xhr.open('POST', opts.api + '/v1/uploads');
          xhr.setRequestHeader('X-Upload-Session', sid);
          xhr.upload.onprogress = function (e) { if (e.lengthComputable) setStatus(item, 'uploading', Math.round(e.loaded / e.total * 100), Math.round(e.loaded / e.total * 100) + '%'); };
          xhr.onload = function () {
            var data = {}; try { data = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
            if (xhr.status >= 200 && xhr.status < 300 && data.file_gid) res(data.file_gid); else rej(new Error(String(xhr.status)));
          };
          xhr.onerror = function () { rej(new Error('network')); };
          var fd = new FormData(); fd.append('file', item.file, item.file.name); xhr.send(fd);
        });
      }).then(function (gid) { item.gid = gid; setStatus(item, 'done', 100, 'Uploaded'); })
        .catch(function () { if (item.status !== 'removed') setStatus(item, 'error', 0, 'Upload failed'); });
    }
    this.remove = function (item) {
      if (!item) return;
      item.status = 'removed'; if (item.xhr) try { item.xhr.abort(); } catch (e) { /* ignore */ }
      URL.revokeObjectURL(item.url); item.node.remove();
      self.items = self.items.filter(function (i) { return i !== item; });
    };
    this.busy = function () { return self.items.some(function (i) { return i.status === 'uploading' || i.status === 'queued'; }); };
    this.gids = function () { return self.items.filter(function (i) { return i.status === 'done' && i.gid; }).map(function (i) { return i.gid; }); };
    this.hasErrors = function () { return self.items.some(function (i) { return i.status === 'error'; }); };
    this.hasMedia = function () { return self.gids().length > 0; };
  }

  /* ---------- rewards config (points for reviews). GET /v1/config → {rewards_enabled, points_review,
     points_media_bonus}; resolves null when disabled or unreachable. Fetched once per page. ---------- */
  var cfgLoad = null;
  function rewardsConfig(base) {
    if (!cfgLoad) cfgLoad = api(base, '/v1/config')
      .then(function (res) { return res.ok && res.data && res.data.rewards_enabled ? res.data : null; })
      .catch(function () { return null; });
    return cfgLoad;
  }

  /* ---------- review card ---------- */
  function cardHTML(r, gallery) {
    var body = r.body || '', long = body.length > 400;
    var h = '<article class="prv-card" data-id="' + esc(r.id) + '"><div class="prv-card__head">' +
      '<span class="prv-card__name">' + esc(r.display_name || 'HUSL customer') + '</span>' +
      (r.verified ? '<span class="prv-card__badge">Verified purchase</span>' : '') +
      (r.featured ? '<span class="prv-card__badge prv-card__badge--featured">Featured</span>' : '') +
      (r.incentivized ? '<span class="prv-card__badge prv-card__badge--points">Earned rewards points</span>' : '') +
      (r.submitted_at ? '<time class="prv-card__date" datetime="' + esc(r.submitted_at) + '">' + esc(fmtDate(r.submitted_at)) + '</time>' : '') +
      '</div><div class="prv-card__stars">' + starsHTML(r.rating, 14) + '</div>';
    if (r.title) h += '<h3 class="prv-card__title">' + esc(r.title) + '</h3>';
    if (body) h += '<p class="prv-card__text"' + (long ? ' data-full="' + esc(body) + '"' : '') + '>' + esc(long ? body.slice(0, 400).replace(/\s+\S*$/, '') + '…' : body) + (long ? ' <button type="button" class="prv-card__more" aria-expanded="false">Read more</button>' : '') + '</p>';
    if (r.purchased_as) h += '<p class="prv-card__meta">Purchased as: ' + esc(r.purchased_as) + '</p>';
    if (r.media && r.media.length) {
      h += '<div class="prv-card__media">';
      r.media.forEach(function (m) {
        var i = gallery.push({ type: m.type, url: m.url, poster: m.poster, alt: (r.title || 'Review') + ' — customer ' + m.type, by: r.display_name }) - 1;
        var thumb = m.type === 'video' ? m.poster : (m.thumb || m.url);
        h += '<button type="button" class="prv-thumb' + (m.type === 'video' ? ' prv-thumb--video' : '') + '" data-g="' + i + '" aria-label="Open ' + (m.type === 'video' ? 'video' : 'photo') + '">' +
          (thumb ? '<img src="' + esc(thumb) + '" alt="" loading="lazy">' : '') + (m.type === 'video' ? '<span class="prv-thumb__play">' + PLAY_SVG + '</span>' : '') + '</button>';
      });
      h += '</div>';
    }
    if (r.reply) h += '<div class="prv-reply"><p class="prv-reply__who">Reply from HUSL Beauty' + (r.reply_at ? ' <time>' + esc(fmtDate(r.reply_at)) + '</time>' : '') + '</p><p class="prv-reply__text">' + esc(r.reply) + '</p></div>';
    return h + '</article>';
  }

  /* ---------- PDP widget ---------- */
  function mountWidget(root) {
    var API = (root.dataset.api || '').replace(/\/$/, ''), PID = root.dataset.productId, SITEKEY = root.dataset.turnstileSitekey || '';
    var TITLE = root.dataset.productTitle || 'this product', POLICY = root.dataset.policyUrl || '/pages/review-policy';
    var summary = q(root, '[data-prv-summary]'), toolbar = q(root, '[data-prv-toolbar]'), list = q(root, '[data-prv-list]'), foot = q(root, '[data-prv-foot]'), formSlot = q(root, '[data-prv-form-slot]');
    var st = { sort: 'recent', rating: null, media: false, cursor: null, gallery: [] };
    var form = null, seq = 0;
    var EMPTY = '<div class="prv-empty-state"><p class="prv-empty">No reviews yet — be the first</p>' + (SITEKEY ? '<button type="button" class="prv-btn prv-btn--solid" data-prv-write>Write a review</button>' : '<p class="prv-fine">Bought this? You\'ll get an email to review it after delivery.</p>') + '</div>';
    if (!SITEKEY) qa(root, '[data-prv-write]').forEach(function (b) { b.remove(); });

    toolbar.innerHTML = '<button type="button" class="prv-chip" data-chip-media aria-pressed="false">With photos &amp; videos</button>' +
      '<label class="prv-sort"><span>Sort</span><select data-sort><option value="recent">Most recent</option><option value="highest">Highest rated</option><option value="lowest">Lowest rated</option><option value="media">With media</option></select></label>';
    toolbar.hidden = false;
    q(toolbar, '[data-chip-media]').addEventListener('click', function () { st.media = !st.media; this.setAttribute('aria-pressed', String(st.media)); load(true); });
    q(toolbar, '[data-sort]').addEventListener('change', function () { st.sort = this.value; load(true); });

    /* Points line under the "Write a review" header — only when the Worker says rewards are on. */
    rewardsConfig(API).then(function (c) {
      var pts = c ? Number(c.points_review) || 0 : 0, bonus = c ? Number(c.points_media_bonus) || 0 : 0, head = q(root, '.prv-head');
      if (!pts || !head) return;
      head.insertAdjacentHTML('afterend', '<p class="prv-points-note">Verified purchasers earn ' + pts + ' HUSL points for a written review' + (bonus ? ' (+' + bonus + ' with a photo)' : '') + ' — any rating.</p>');
    });

    root.addEventListener('click', function (e) {
      var t;
      if ((t = e.target.closest('.prv-hist__row'))) { var r = Number(t.dataset.rating); st.rating = st.rating === r ? null : r; paintHist(); load(true); }
      else if ((t = e.target.closest('[data-prv-write]'))) openForm();
      else if ((t = e.target.closest('.prv-card__more'))) {
        var p = t.parentNode, open = t.getAttribute('aria-expanded') === 'true', full = p.dataset.full;
        p.firstChild.nodeValue = open ? full.slice(0, 400).replace(/\s+\S*$/, '') + '… ' : full + ' ';
        t.textContent = open ? 'Read more' : 'Read less'; t.setAttribute('aria-expanded', String(!open));
      }
      else if ((t = e.target.closest('.prv-thumb'))) LB.open(st.gallery, Number(t.dataset.g), t);
      else if ((t = e.target.closest('[data-prv-more]'))) load(false);
      else if ((t = e.target.closest('[data-prv-retry]'))) load(!st.cursor);
    });

    function paintHist() {
      qa(summary, '.prv-hist__row').forEach(function (b) { var on = Number(b.dataset.rating) === st.rating; b.setAttribute('aria-pressed', String(on)); b.classList.toggle('is-on', on); });
      summary.classList.toggle('is-filtered', st.rating != null);
    }
    /* Same markup Liquid renders from the metafields, so hydration is a no-op visually. */
    function renderSummary(s) {
      var count = Number(s.count) || 0, hist = s.histogram || {};
      toolbar.hidden = !count;
      if (!count) { summary.innerHTML = EMPTY; return; }
      var h = '<div class="prv-summary__score"><span class="prv-summary__avg">' + (Number(s.average) || 0).toFixed(1) + '</span>' + starsHTML(s.average, 20) +
        '<span class="prv-summary__count">Based on ' + plural(count, 'review') + '</span></div><div class="prv-hist" role="group" aria-label="Filter by star rating">';
      for (var star = 5; star >= 1; star--) {
        var n = Number(hist[star]) || 0, pct = Math.round(n / count * 100);
        h += '<button type="button" class="prv-hist__row" data-rating="' + star + '" aria-pressed="false" aria-label="' + plural(star, 'star') + ': ' + plural(n, 'review') + '">' +
          '<span class="prv-hist__label">' + star + '<span aria-hidden="true">&#9733;</span></span><span class="prv-hist__bar"><span class="prv-hist__fill" style="width:' + pct + '%;"></span></span><span class="prv-hist__n">' + n + '</span></button>';
      }
      summary.innerHTML = h + '</div>';
      var chip = q(toolbar, '[data-chip-media]');
      if (chip && s.media_count != null) chip.textContent = 'With photos & videos (' + s.media_count + ')';
      paintHist();
    }
    /* Latest request wins: a filter/sort change while a load is in flight supersedes it. */
    function load(reset) {
      var my = ++seq;
      if (reset) { st.cursor = null; list.innerHTML = ''; st.gallery = []; }
      foot.innerHTML = '<p class="prv-loading">Loading reviews…</p>';
      var qs = '?product_id=' + encodeURIComponent(PID) + '&sort=' + st.sort + '&limit=10' + (st.rating ? '&rating=' + st.rating : '') + (st.media ? '&media=1' : '') + (st.cursor ? '&cursor=' + encodeURIComponent(st.cursor) : '');
      api(API, '/v1/reviews' + qs).then(function (res) {
        if (my !== seq) return;
        if (!res.ok) throw new Error(String(res.status));
        var d = res.data || {}, reviews = d.reviews || [];
        if (d.summary) renderSummary(d.summary);
        list.insertAdjacentHTML('beforeend', reviews.map(function (r) { return cardHTML(r, st.gallery); }).join(''));
        st.cursor = d.next_cursor || null;
        if (st.cursor) foot.innerHTML = '<button type="button" class="prv-btn prv-btn--outline" data-prv-more>Load more reviews</button>';
        else if (!list.children.length && (st.rating != null || st.media)) foot.innerHTML = '<p class="prv-empty">No reviews match these filters.</p>';
        else foot.innerHTML = '';
      }).catch(function () {
        if (my === seq) foot.innerHTML = '<p class="prv-empty">Couldn\'t load reviews right now. <button type="button" class="prv-link" data-prv-retry>Try again</button></p>';
      });
    }

    /* ----- inline write form ----- */
    function openForm() {
      if (!SITEKEY) return;
      if (!form) form = buildForm();
      formSlot.hidden = false;
      formSlot.scrollIntoView({ behavior: 'smooth', block: 'start' });
      q(form.node, '.prv-pick__star').focus();
    }
    function closeForm() { formSlot.hidden = true; var b = q(root, '[data-prv-write]'); if (b) b.focus(); }
    function buildForm() {
      var node = el('<form class="prv-form" novalidate>' +
        '<div class="prv-form__head"><h3 class="prv-form__title">Write a review</h3><p class="prv-form__sub">' + esc(TITLE) + '</p></div>' +
        '<div class="prv-step" data-step="rating"><p class="prv-label">Your rating</p><div data-picker></div></div>' +
        '<div class="prv-step" data-step="rest" hidden>' +
          '<label class="prv-field"><span class="prv-label">Title <em>(optional)</em></span><input name="title" maxlength="100" autocomplete="off"></label>' +
          '<label class="prv-field"><span class="prv-label">Your review <em>(optional)</em></span><textarea name="body" maxlength="5000" rows="4" placeholder="What did you use it for? How did it go?"></textarea></label>' +
          '<div class="prv-field"><span class="prv-label">Photos or video <em>(optional)</em></span><div data-uploader></div></div>' +
          '<div class="prv-grid2"><label class="prv-field"><span class="prv-label">Your name</span><input name="name" maxlength="80" autocomplete="name" required></label>' +
          '<label class="prv-field"><span class="prv-label">Email</span><input name="email" type="email" autocomplete="email" required></label></div>' +
          '<p class="prv-fine">Shown as your first name and last initial. Your email is never published — if it matches an order for this product, your review gets a Verified purchase badge and your rating counts straight away.</p>' +
          '<div class="prv-hp" aria-hidden="true"><label>Leave this empty <input name="prv_extra_field" tabindex="-1" autocomplete="off" data-lpignore="true" data-1p-ignore data-form-type="other"></label></div>' +
          '<div class="prv-ts" data-ts></div>' +
          '<p class="prv-form__err" role="alert"></p>' +
          '<div class="prv-form__actions"><button type="submit" class="prv-btn prv-btn--solid">Submit review</button><button type="button" class="prv-btn prv-btn--ghost" data-cancel>Cancel</button></div>' +
          '<p class="prv-fine">Written reviews and photos are checked by our team before they go live — usually within a couple of days. We publish negative reviews too and never edit your words. <a href="' + esc(POLICY) + '">Review policy</a></p>' +
        '</div></form>');
      var rest = q(node, '[data-step="rest"]'), errEl = q(node, '.prv-form__err'), submitBtn = q(node, 'button[type="submit"]');
      function f(n) { return q(node, '[name="' + n + '"]'); } /* form.name/form.title are the form's own attributes */
      // Turnstile is mounted only once the rest of the form is visible — rendering it inside a hidden
      // container makes Cloudflare's widget unreliable (no token, or a late one).
      var ts = null;
      function getTs() { if (!ts) ts = turnstileWidget(q(node, '[data-ts]'), SITEKEY); return ts; }
      var picker = starPicker('Your rating', function (v) { if (v && rest.hidden) { rest.hidden = false; getTs(); f('title').focus(); } });
      q(node, '[data-picker]').appendChild(picker.node);
      /* Logged-in customer (window.__huslCustomer from templates/product.liquid): pre-fill, lock the email. */
      var cust = window.__huslCustomer && typeof window.__huslCustomer === 'object' ? window.__huslCustomer : null;
      if (cust) {
        var fullName = [cust.first_name, cust.last_name].filter(Boolean).join(' ').trim();
        if (fullName) f('name').value = fullName;
        if (cust.email) { f('email').value = cust.email; f('email').readOnly = true; }
      }
      var up = new Uploader({ api: API, getSession: function () {
        return getTs().take().then(function (tok) { return api(API, '/v1/upload-session', { method: 'POST', body: { turnstile_token: tok } }); })
          .then(function (res) { if (!res.ok || !res.data.session_id) throw new Error('session'); return res.data.session_id; });
      } });
      q(node, '[data-uploader]').appendChild(up.node);
      q(node, '[data-cancel]').addEventListener('click', closeForm);
      function fail(msg) { errEl.textContent = msg; submitBtn.disabled = false; submitBtn.textContent = 'Submit review'; }
      node.addEventListener('submit', function (e) {
        e.preventDefault(); errEl.textContent = '';
        var rating = picker.get(), name = f('name').value.trim(), email = f('email').value.trim();
        var title = f('title').value.trim(), body = f('body').value.trim();
        if (!rating) return fail('Tap a star to rate ' + TITLE + '.');
        if (!name) return fail('Please add your name.');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return fail('Please enter a valid email address.');
        if (up.busy()) return fail('Please wait for your photos to finish uploading.');
        if (up.hasErrors()) return fail('One of your photos didn\'t upload. Remove it (×) or try adding it again.');
        submitBtn.disabled = true; submitBtn.textContent = 'Verifying…';
        getTs().take().then(function (tok) {
          submitBtn.textContent = 'Submitting…';
          var payload = { product_id: PID, rating: rating, title: title, body: body, name: name, email: email, media: up.gids(), turnstile_token: tok, website: f('prv_extra_field').value };
          if (cust && cust.id && cust.key) { payload.customer_id = String(cust.id); payload.account_key = String(cust.key); }
          return api(API, '/v1/reviews', { method: 'POST', body: payload });
        }).then(function (res) {
          if (res.ok) return success(res.data || {}, !!(title || body || up.hasMedia()));
          if (res.status === 409) return fail('Looks like you\'ve reviewed this product in the last 30 days — thank you! One review per product every 30 days keeps things fair.');
          if (res.status === 403) return fail('We weren\'t able to accept this review. If you think that\'s a mistake, email hello@huslbeauty.com.');
          if (res.status === 429) return fail('Too many attempts — please wait a few minutes and try again.');
          fail((res.data && res.data.message) || GENERIC_ERR);
        }).catch(function (err) {
          fail(err && /^turnstile/.test(err.message) ? 'We couldn\'t verify you\'re human — please refresh the page and try again.' : GENERIC_ERR);
        });
      });
      function success(data, hasContent) {
        var verified = data.verified === true || data.is_verified === true;
        var msg = hasContent
          ? 'Our team checks written reviews and photos before they go live (usually within a couple of days).' + (verified ? ' Your star rating already counts.' : '')
          : verified ? 'It already counts in the score above.' : 'Your rating will show once our team has checked it (usually within a couple of days).';
        formSlot.innerHTML = '<div class="prv-success" role="status"><p class="prv-success__title">Thanks' + (verified && !hasContent ? ' — your rating is live' : '') + '!</p><p>' + msg + '</p><button type="button" class="prv-btn prv-btn--outline" data-done>Done</button></div>';
        q(formSlot, '[data-done]').focus();
        q(formSlot, '[data-done]').addEventListener('click', function () { formSlot.hidden = true; });
        form = null;
        if (verified) load(true);
      }
      formSlot.innerHTML = ''; formSlot.appendChild(node);
      return { node: node };
    }

    load(true);
  }

  /* ---------- token form page (/pages/product-review?token=…) ---------- */
  function mountTokenForm(root) {
    var API = (root.dataset.worker || '').replace(/\/$/, '');
    var token = new URLSearchParams(location.search).get('token');
    var items = [];
    function show(s) { qa(root, '.hpr__state').forEach(function (n) { n.classList.toggle('is-active', n.dataset.state === s); }); }
    function getSession() {
      return api(API, '/v1/upload-session', { method: 'POST', body: { token: token } })
        .then(function (res) { if (!res.ok || !res.data.session_id) throw new Error('session'); return res.data.session_id; });
    }
    if (!token) return show('invalid');

    api(API, '/v1/submit?token=' + encodeURIComponent(token)).then(function (res) {
      if (res.status === 410) return show('expired');
      if (!res.ok) return show('invalid');
      var d = res.data || {}, first = (d.customer_first_name || '').trim();
      q(root, '[data-bind="greeting"]').textContent = first ? 'How did they go, ' + first + '?' : 'How did they go?';
      var wrap = q(root, '#hpr-items');
      (d.products || []).forEach(function (p) {
        var node = el('<div class="hpr__item' + (p.already_reviewed ? ' is-done' : '') + '"><div class="hpr__item-head">' +
          (p.image ? '<img class="hpr__thumb" src="' + esc(p.image) + '" alt="">' : '<div class="hpr__thumb"></div>') +
          '<p class="hpr__pname">' + esc(p.title || 'Product') + '</p></div>' +
          (p.already_reviewed ? '<p class="hpr__done">&#10003; Already reviewed — thank you</p>' :
            '<div data-picker></div><div class="hpr__more" hidden>' +
            '<input class="hpr__in" maxlength="100" placeholder="Title (optional)" aria-label="Review title">' +
            '<textarea class="hpr__ta" maxlength="5000" placeholder="Optional: a few words — checked by our team before publishing" aria-label="Your review"></textarea>' +
            '<div data-uploader></div></div>') + '</div>');
        wrap.appendChild(node);
        if (p.already_reviewed) return;
        var more = q(node, '.hpr__more');
        var picker = starPicker('Rating for ' + (p.title || 'product'), function (v) { if (v) { more.hidden = false; q(root, '[data-error="none"]').classList.remove('show'); } });
        q(node, '[data-picker]').appendChild(picker.node);
        var up = new Uploader({ api: API, getSession: getSession });
        q(node, '[data-uploader]').appendChild(up.node);
        items.push({ product_id: String(p.product_id), picker: picker, node: node, up: up });
      });
      if (!items.length && (d.products || []).length) { q(root, '#hpr-submit').hidden = true; q(root, '[data-bind="all-done"]').hidden = false; }
      show('form');
    }).catch(function () { show('invalid'); });

    q(root, '#hpr-form').addEventListener('submit', function (e) {
      e.preventDefault();
      var noneErr = q(root, '[data-error="none"]'), subErr = q(root, '[data-error="submit"]'), btn = q(root, '#hpr-submit');
      subErr.classList.remove('show');
      var hasContent = false, reviews = [];
      items.forEach(function (it) {
        var v = it.picker.get(); if (!v) return;
        var title = q(it.node, '.hpr__in').value.trim(), body = q(it.node, '.hpr__ta').value.trim(), media = it.up.gids();
        if (title || body || media.length) hasContent = true;
        var r = { product_id: it.product_id, rating: v };
        if (title) r.title = title; if (body) r.body = body; if (media.length) r.media = media;
        reviews.push(r);
      });
      if (!reviews.length) return noneErr.classList.add('show');
      if (items.some(function (it) { return it.up.busy(); })) { subErr.textContent = 'Please wait for your photos to finish uploading.'; return subErr.classList.add('show'); }
      if (items.some(function (it) { return it.up.hasErrors(); })) { subErr.textContent = 'One of your photos didn\'t upload. Remove it (×) or try adding it again.'; return subErr.classList.add('show'); }
      btn.disabled = true; btn.textContent = 'Submitting…';
      api(API, '/v1/submit', { method: 'POST', body: { token: token, reviews: reviews } }).then(function (res) {
        if (res.ok) {
          q(root, '[data-bind="success-copy"]').textContent = hasContent
            ? 'Your star ratings are live now. Written notes and photos appear once our team has checked them — usually within a couple of days.'
            : 'Your ratings are live. Thanks for helping other shoppers find the good stuff.';
          return show('success');
        }
        if (res.status === 410) return show('expired');
        throw new Error(String(res.status));
      }).catch(function () { subErr.textContent = GENERIC_ERR; subErr.classList.add('show'); btn.disabled = false; btn.textContent = 'Submit review'; });
    });
  }

  function boot() {
    var w = document.getElementById('husl-reviews'); if (w && w.dataset.productId) mountWidget(w);
    var t = document.getElementById('hpr'); if (t) mountTokenForm(t);
  }
  window.HUSLReviews = { mountWidget: mountWidget, mountTokenForm: mountTokenForm, lightbox: LB };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
